/**
 * WeaveIn.ai Platform — Member Activity Feed API Service
 *
 * TRUS-420. Backs the member Activity screen: the merged note/system feed, the
 * per-CBO note category catalogue, and note create/edit with tags.
 *
 * Uses the absolute production API base like the other services — never a
 * relative /api path, which Vercel proxies to the dev backend.
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

export interface NoteCategory {
  id: number;
  cbo_id: number;
  name: string;
  /** Stable key. Surfaces match on this, so renaming `name` is safe. */
  slug: string;
  color?: string | null;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
  /** When true this category renders as its own member-profile section. */
  show_as_section: boolean;
  note_count?: number | null;
}

/** What the row is. `origin` is the coarser split the UI renders differently.
 *  TRUS-762 widened the feed from four sources to every member record kind;
 *  the same values are what `types` accepts. */
export type FeedKind =
  | 'note'
  | 'activity'
  | 'task'
  | 'document'
  | 'form'
  | 'message'
  | 'outreach'
  | 'letter'
  | 'encounter'
  | 'referral'
  | 'workflow'
  | 'member_update';
export type FeedOrigin = 'note' | 'system';

/** Every kind, in the order the API documents them. */
export const FEED_KINDS: FeedKind[] = [
  'note', 'activity', 'task', 'document', 'form', 'message',
  'outreach', 'letter', 'encounter', 'referral', 'workflow', 'member_update',
];

/** `case` filter: every record, records with no case, or one case by id. */
export type FeedCaseFilter = 'all' | 'none' | number;

/** One of the member's cases, as the feed returns them for the case bar (TRUS-762). */
export interface FeedCase {
  id: number;
  case_number: string;
  episode_number?: number | null;
  status: string;
  is_current: boolean;
  program_id: number;
  program_name?: string | null;
  enrollment_id?: number | null;
  opened_date?: string | null;
  closed_date?: string | null;
}

/** Body of POST /api/members/{id}/records/move-case. null = "no case". */
export interface MoveRecordCasePayload {
  record_type: string;
  record_id: number;
  case_episode_id: number | null;
}

export interface ActivityFeedItem {
  /** Namespaced ("note:12") — ids are not unique across the merged sources. */
  id: string;
  kind: FeedKind;
  origin: FeedOrigin;
  occurred_at: string;
  title?: string | null;
  body?: string | null;
  author_id?: number | null;
  author_name?: string | null;
  categories: NoteCategory[];
  meta: Record<string, any>;
  editable: boolean;
  // TRUS-762 (Phase 2). Optional: an older API omits them, and the UI must
  // still render the row.
  /** null = the record belongs to no case. */
  case_episode_id?: number | null;
  case_number?: string | null;
  program_id?: number | null;
  program_name?: string | null;
  enrollment_id?: number | null;
  /** Value move-case accepts; null when the row cannot be moved (message, member_update, workflow). */
  record_type?: string | null;
  /** Int id for move-case and for opening the record. */
  record_id?: number | null;
  /** Kind-specific status (task status, form status, referral status…). */
  status?: string | null;
}

/** A file hung off a case note (TRUS-520). No name, no category — just the file. */
export interface NoteAttachment {
  id: number;
  note_id: number;
  filename: string;
  mime_type?: string | null;
  file_size_bytes?: number | null;
  uploaded_by?: number | null;
  uploaded_by_name?: string | null;
  created_at: string;
}

export interface ActivityFeedResponse {
  member_id: number;
  items: ActivityFeedItem[];
  /** What this window pulled — NOT a total across all sources. Paginate on has_more. */
  merged_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
  // TRUS-762 (Phase 2), all optional so an older API still renders.
  /** True per-kind counts under the same filters except `types`. */
  counts?: Partial<Record<FeedKind, number>>;
  /** All of this member's cases in the caller's org, newest first. */
  cases?: FeedCase[];
  /** Whether the caller may move records between cases (org admins and managers). */
  can_move?: boolean;
}

export interface NoteCategoryList {
  items: NoteCategory[];
  /** Server's answer on whether this caller may edit the list. Authorization is
   *  on the ORG role, which the client cannot reconstruct — never guess it. */
  can_manage: boolean;
}

export interface CreateNotePayload {
  note_text: string;
  subject?: string;
  note_type?: string;
  enrollment_id?: number;
  category_ids?: number[];
  is_confidential?: boolean;
  /** TRUS-762: file the note under this case; absent = derived from the enrollment. */
  case_episode_id?: number | null;
}

export interface UpdateNotePayload {
  note_text?: string;
  subject?: string;
  /** Omit to leave tags untouched; pass [] to clear them. */
  category_ids?: number[];
}

// ============================================================================
// CLIENT
// ============================================================================

class MemberActivityFeedAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('MemberActivityFeed API Error:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.response?.data?.detail || error.message,
        });
        return Promise.reject(error);
      }
    );
  }

  // ---- categories ---------------------------------------------------------

  async listCategories(includeInactive = false): Promise<NoteCategoryList> {
    const res = await this.client.get('/api/note-categories', {
      params: includeInactive ? { include_inactive: true } : undefined,
    });
    return res.data;
  }

  async createCategory(
    name: string,
    color?: string,
    showAsSection = false
  ): Promise<NoteCategory> {
    const res = await this.client.post('/api/note-categories', {
      name,
      color,
      show_as_section: showAsSection,
    });
    return res.data;
  }

  async updateCategory(
    id: number,
    patch: {
      name?: string;
      color?: string;
      sort_order?: number;
      is_active?: boolean;
      show_as_section?: boolean;
    }
  ): Promise<NoteCategory> {
    const res = await this.client.patch(`/api/note-categories/${id}`, patch);
    return res.data;
  }

  /** Soft delete — hides it from the picker; notes already tagged keep the tag. */
  async deleteCategory(id: number): Promise<{ status: string; notes_still_tagged: number }> {
    const res = await this.client.delete(`/api/note-categories/${id}`);
    return res.data;
  }

  // ---- feed ---------------------------------------------------------------

  /** The API caps `limit` at 200 (Query(le=200)); anything larger is a 422. */
  static readonly MAX_LIMIT = 200;

  async getFeed(
    memberId: number,
    opts: {
      categoryId?: number | null;
      origin?: FeedOrigin | null;
      limit?: number;
      offset?: number;
      /** Only these kinds. Absent or empty = every kind. */
      types?: FeedKind[] | null;
      /** 'all' is the API default, so it is not sent. */
      caseFilter?: FeedCaseFilter | null;
      programId?: number | null;
      enrollmentId?: number | null;
      dateFrom?: string | null;
      dateTo?: string | null;
    } = {}
  ): Promise<ActivityFeedResponse> {
    const params: Record<string, any> = {
      limit: Math.min(opts.limit ?? 50, MemberActivityFeedAPI.MAX_LIMIT),
      offset: opts.offset ?? 0,
    };
    if (opts.categoryId) params.category_id = opts.categoryId;
    if (opts.origin) params.origin = opts.origin;
    if (opts.types && opts.types.length) params.types = opts.types.join(',');
    if (opts.caseFilter != null && opts.caseFilter !== 'all') params.case = String(opts.caseFilter);
    if (opts.programId) params.program_id = opts.programId;
    if (opts.enrollmentId) params.enrollment_id = opts.enrollmentId;
    if (opts.dateFrom) params.date_from = opts.dateFrom;
    if (opts.dateTo) params.date_to = opts.dateTo;
    const res = await this.client.get(`/api/members/${memberId}/activity-feed`, { params });
    return res.data;
  }

  // ---- cases (TRUS-762) ---------------------------------------------------

  /** Re-point one record at another of the member's cases, or at none (null).
   *  Org admins and managers only; anyone else gets a 403. */
  async moveRecordCase(memberId: number, payload: MoveRecordCasePayload) {
    const res = await this.client.post(`/api/members/${memberId}/records/move-case`, payload);
    return res.data;
  }

  // ---- notes --------------------------------------------------------------

  async createNote(memberId: number, payload: CreateNotePayload) {
    const res = await this.client.post(`/api/members/${memberId}/notes`, payload);
    return res.data;
  }

  async updateNote(noteId: number, payload: UpdateNotePayload) {
    const res = await this.client.patch(`/api/member-notes/${noteId}`, payload);
    return res.data;
  }

  // ---- attachments (TRUS-520) --------------------------------------------
  //
  // Deliberately NOT member documents: reaching that store means picking a category
  // and a sub-type first, and the ask was explicitly "do not need to name the doc or
  // categorize as long as its attached to the case note".

  async listNoteAttachments(noteId: number): Promise<NoteAttachment[]> {
    const res = await this.client.get(`/api/member-notes/${noteId}/attachments`);
    return res.data;
  }

  async uploadNoteAttachment(noteId: number, file: File): Promise<NoteAttachment> {
    const form = new FormData();
    form.append('file', file);
    // Let the browser set the multipart boundary; a hand-set Content-Type omits it
    // and the request arrives unparseable.
    const res = await this.client.post(`/api/member-notes/${noteId}/attachments`, form, {
      headers: { 'Content-Type': undefined as unknown as string },
    });
    return res.data;
  }

  async deleteNoteAttachment(noteId: number, attachmentId: number): Promise<void> {
    await this.client.delete(`/api/member-notes/${noteId}/attachments/${attachmentId}`);
  }

  /** Streamed through the backend, so the fetch has to carry the auth header. */
  async downloadNoteAttachment(noteId: number, attachmentId: number): Promise<Blob> {
    const res = await this.client.get(
      `/api/member-notes/${noteId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    return res.data as Blob;
  }

  // Category sections read through getFeed({categoryId, origin:'note'}) — one
  // read path rather than a second endpoint returning the same rows.
}

export const memberActivityFeedAPI = new MemberActivityFeedAPI();
export default memberActivityFeedAPI;
